import React, { useEffect, useState } from 'react';
import { TELL_ANTHONY, canStopJob, jobStatusText, jobTone, stopConfirmText, visibleJobs } from '../../../../services/trackJobsModel';
import { vocalProgress } from './trackStripModel';
import './JobLines.css';

/** Where a failed job's recording came from, for the detail line. */
const sourceOf = (job) => {
	if (!job || !job.source) return '';
	if (job.source.url) return job.source.url;
	if (job.source.fileName) return job.source.fileName;
	return '';
};

/**
 * "Working now" (Lesson Dashboard pass 3 F1/F3/F4, 2026-09-28): one line per
 * track job worth seeing, for this song. Every kind lists here, save-key and
 * lyrics-timing included; until this pass those lived inside Add a track and
 * vanished wherever Add a track did (no student linked).
 *
 * A FAILED ADD (an upload or a link) gets the full treatment, because it is
 * the one a teacher must act on: "The track could not be added. Please tell
 * Anthony right away." large, the cause, job id and source small beneath it,
 * **Try again** (the same link re-queued; for an upload the file has to be
 * chosen again, so it reopens Add a track), **Dismiss**, and the line that
 * answers the question a teacher actually has: "Nothing was saved. Your notes
 * and the lesson are fine."
 *
 * A vocal-removal job a worker has claimed shows a bar paced by the song's own
 * length (trackStripModel.vocalProgress); a job with nothing honest to pace it
 * by shows the spinner and its line only.
 *
 * `dismissed` is the PAGE's set, shared with the strip's job chip, so a
 * dismissed failure clears from both.
 *
 * **Stop** (2026-10-02, Anthony: "a way to stop a track from processing in
 * case one is started by mistake. this should clear anything already taking
 * up space"): on a job a teacher started that is still waiting or running.
 * One tap asks, naming what is thrown away; the second calls `onStop(job)`
 * (cancelTrackJob), and the row leaves on its own when the job reads
 * `cancelled`. A failure says so in the owner's words.
 */
const JobLines = ({ jobs = [], anyMacOn = true, nowMs = null, dismissed = new Set(), onDismiss, onRetry = null, onStop = null, askFor = null }) => {
	const now = nowMs || Date.now();
	const [asking, setAsking] = useState(null); // job id whose Stop is being confirmed
	const [stopping, setStopping] = useState(null);
	const [stopError, setStopError] = useState(null); // { id, message }
	// Stop tapped on the Song panel's Tracks row (pass 4 S5.1) arrives here
	// asked: the confirmation is open on that job, one tap from done.
	useEffect(() => {
		if (askFor && askFor.id) setAsking(askFor.id);
	}, [askFor]);
	const stop = async (job) => {
		setStopping(job.id);
		setStopError(null);
		try {
			await onStop(job);
			setAsking(null);
		} catch (e) {
			setStopError({ id: job.id, message: (e && e.message) || 'unknown error' });
		} finally {
			setStopping(null);
		}
	};
	const rows = visibleJobs(jobs, now, dismissed);
	if (!rows.length) return null;
	return (
		<ul className="ld-jobs" aria-live="polite">
			{rows.map((j) => {
				const tone = jobTone(j);
				const isAdd = !j.kind || j.kind === 'link';
				if (tone === 'failed' && isAdd) {
					const detail = [j.error, `job ${j.id}`, sourceOf(j)].filter(Boolean).join(' · ');
					return (
						<li key={j.id} className="ld-jobs__fail" role="alert">
							<p className="ld-jobs__failhead">
								<i className="fa-sharp fa-solid fa-triangle-exclamation" aria-hidden="true" />
								The track could not be added. {TELL_ANTHONY}
							</p>
							<p className="ld-jobs__detail">{detail}</p>
							<p className="ld-jobs__safe">Nothing was saved. Your notes and the lesson are fine.</p>
							<div className="ld-jobs__actions">
								{onRetry && (
									<button type="button" className="ld-jobs__btn ld-jobs__btn--primary" onClick={() => onRetry(j)}>
										Try again
									</button>
								)}
								<button type="button" className="ld-jobs__btn" onClick={() => onDismiss && onDismiss(j.id)}>
									Dismiss
								</button>
							</div>
						</li>
					);
				}
				const icon = tone === 'done' ? 'fa-circle-check' : tone === 'failed' ? 'fa-triangle-exclamation' : tone === 'stopped' ? 'fa-circle-stop' : 'fa-spinner-third fa-spin';
				const progress = tone === 'working' ? vocalProgress(j, now) : null;
				return (
					<li key={j.id} className={`ld-jobs__row ld-jobs__row--${tone}`}>
						<i className={`fa-sharp fa-solid ${icon}`} aria-hidden="true" />
						<span className="ld-jobs__text">
							{jobStatusText(j, { anyMacOn })}
							{tone === 'failed' && j.error && <span className="ld-jobs__detail">{j.error}</span>}
							{progress != null && (
								<span
									className="ld-jobs__bar"
									role="progressbar"
									aria-label="Removing the vocals"
									aria-valuemin={0}
									aria-valuemax={100}
									aria-valuenow={Math.round(progress * 100)}>
									<span className="ld-jobs__fill" style={{ width: `${Math.round(progress * 100)}%` }} />
								</span>
							)}
						</span>
						{tone === 'failed' && (
							<button type="button" className="ld-dismiss" onClick={() => onDismiss && onDismiss(j.id)}>
								Dismiss
							</button>
						)}
						{onStop && canStopJob(j) && asking !== j.id && (
							<button type="button" className="ld-jobs__btn ld-jobs__stop" onClick={() => setAsking(j.id)}>
								<i className="fa-sharp fa-solid fa-circle-stop" aria-hidden="true" />
								Stop
							</button>
						)}
						{onStop && canStopJob(j) && asking === j.id && (
							<div className="ld-jobs__confirm" role="group" aria-label="Stop this track">
								{(() => {
									const t = stopConfirmText(j);
									return (
										<>
											<p className="ld-jobs__ask">{t.head}</p>
											<p className="ld-jobs__goes">
												<b>Thrown away:</b> {t.thrown}
											</p>
											<p className="ld-jobs__goes">
												<b>Kept:</b> {t.kept}
											</p>
										</>
									);
								})()}
								<div className="ld-jobs__actions">
									<button type="button" className="ld-jobs__btn ld-jobs__btn--danger" disabled={stopping === j.id} onClick={() => stopping !== j.id && stop(j)}>
										{stopping === j.id ? 'Stopping…' : 'Stop and delete'}
									</button>
									<button type="button" className="ld-jobs__btn" onClick={() => setAsking(null)}>
										Keep going
									</button>
								</div>
								{stopError && stopError.id === j.id && (
									<div role="alert">
										<p className="ld-jobs__failhead">The track could not be stopped. {TELL_ANTHONY}</p>
										<p className="ld-jobs__detail">{[stopError.message, `job ${j.id}`].join(' · ')}</p>
									</div>
								)}
							</div>
						)}
					</li>
				);
			})}
		</ul>
	);
};

export default JobLines;
